import React, { useEffect, useState } from 'react'
import App from '../App'
import CenterHead from '../organisms/CenterHead/CenterHead.jsx'
import ContactBar from '../organisms/StickyBar/ContactBar'
import PremiumCenter from './PremiumCenter'
import Spacer from '../atoms/Spacer/Spacer'
import StandardCenter from './StandardCenter'
import NavMarkers from '../organisms/StickyBar/NavMarkers'
import RelatedLinks from '../organisms/RelatedLinks'
import { ZoneIds } from '../../domain/zoneIds'
import useSegment from '../../hooks/segment/useSegment'
import computeIsLuxury from '@/domain/useCases/center/computeIsLuxury'
import {
  getPhoneNumbers,
  isPremiumListing,
  isFreeListing
} from '../../domain/center.js'
import { AppContextProvider } from '@/components/AppContextProvider'
import useDidMount from '../../hooks/useDidMount.jsx'
import DefaultCenterFAQs from '../organisms/AccordionFAQs/DefaultCenterFAQs'
import CustomFAQs from '../organisms/AccordionFAQs/CustomFAQs'
import { MAX_HIT_COUNT } from '../../lib/constants'
import { generateSocialTags } from '../../domain/seo/useCases/generateSocialTags.js'
import MetaData from '../SEO/MetaData'
import { filterFAQs } from '../../domain/FAQs/filterFAQs'
import { generateDefaultCenterFaq } from '../../domain/FAQs/generateDefaultFAQs.js'
import { Center, Page, Location as LocationData } from '../../types/models'
import PageContext from '../../types/PageContext'
import pushToDataLayer from '../../utils/dataLayer'
import { getFeaturedCentersRelatedToCenterLocation } from '../../domain/center/useCases/getFeaturedCentersRelatedToCenterLocation'
import centerSchema from '../../utils/schema/centerSchema'
import CenterCarousel from '../molecules/CenterCarousel/CenterCarousel'
import { Department } from '@/domain/department'
import { getCenterPageDepartment } from '@/domain/department'
import { getCenterProfileSlug } from '@/domain/center'
import {
  isNonEmptyArray,
  isNonEmptyObject,
  isNonEmptyString
} from '@/utils/checks'
import { MILE_IN_METERS } from '@/utils/computeMilesToMeters'
import logger from '@/utils/logger'
const NEARBY_AROUND_RADIUS_IN_METERS = MILE_IN_METERS * 750
import { LocationAllowList } from '@/types/models/Location.model'
const HOST = 'https://recovery.com' // we need it hardcoded for SEO reasons

interface CenterProps {
  center: Center
  reviews: []
  ourCenters: { node: Center }[]
  locationData: LocationData
  location: Location //This is window.location type
  pageContext: PageContext
  ignorePpv?: boolean
  locationAllowList: LocationAllowList
  departments?: []
}

const CenterComponent: React.FC<CenterProps> = (props) => {
  const didMount = useDidMount()
  const { center = {} as Center } = props

  const department: Department = getCenterPageDepartment({
    center,
    location: center.locations,
    term: undefined
  })

  const centerUrl = `${HOST}${getCenterProfileSlug(center)}`

  const [isContactBarVisible, setIsContactBarVisible] = useState(false)
  const segment = useSegment()

  const listingTemplate = center.listing_template

  const TemplateCenter = isPremiumListing(center)
    ? PremiumCenter
    : StandardCenter

  useEffect(() => {
    if (props.ignorePpv) {
      return
    }
    segment.profilePage.ppv({
      title: center.title,
      slug: center.slug,
      br: center.br || null,
      sponsor_tier: center.sponsor_tier
    })
  }, [])

  useEffect(() => {
    pushToDataLayer({
      event: 'page_view_datalayer_ready',
      ...props.pageContext.dataLayer
    })
  }, [])

  let queryID
  if (didMount) {
    queryID = window.localStorage.getItem('queryID') || ''
  }

  const handleScroll = () => {
    const contactBoxElement = document.getElementById('contact-box')

    if (!contactBoxElement) {
      return
    }

    const contactBoxElementDomRect = contactBoxElement.getBoundingClientRect()
    const intersection =
      contactBoxElementDomRect.top + contactBoxElementDomRect.height + 65

    if (window.innerHeight > intersection && !isContactBarVisible) {
      setIsContactBarVisible(true)
    }

    if (window.innerHeight < intersection && isContactBarVisible) {
      setIsContactBarVisible(false)
    }
  }

  const getCenterDataKeywords = () => {
    const keywords = [
      ...(center.locations || []),
      ...(center.new_specializations || []),
      ...(center.onsite_activities || []),
      ...(center.off_site_activities || []),
      ...(center.treatment_method || []),
      ...(center.languages || []),
      ...(center.price_length || []),
      ...(center.treatment_therapies || []),
      ...(center.aftercare || []),
      ...(center.arranged_aftercare || []),
      ...(center.included_aftercare || []),
      ...(center.special_considerations || []),
      ...(center.who_we_treat || []),
      ...(center.what_we_treat || []),
      ...(center.onsite_amenities || []),
      ...(center.off_amenities || []),
      ...(center.personal_amenities || []),
      ...(center.onsite_activities || []),
      ...(center.off_site_activities || []),
      props.location.href,
      'page_/' + center.slug
    ]

    return keywords
  }

  useEffect(() => {
    if (listingTemplate === 'Premium') {
      window.addEventListener('scroll', handleScroll)
    }
  })

  const { locations = [], new_locations = [], treatment_services = [] } = center

  const { offersDetox, detoxServiceIndex } = treatment_services.reduce(
    (acc, service, index) => {
      if (service.includes('Detox')) {
        acc.detoxServiceIndex = index
        acc.offersDetox = true
      }
      return acc
    },
    { offersDetox: false, detoxServiceIndex: NaN }
  )

  const breadcrumbs =
    new_locations?.length > 0
      ? new_locations
          ?.filter((location) => location)
          ?.map((location) => (location.title ? location.title : ''))
      : locations

  let breadCrumbSchema = {}
  if (breadcrumbs?.length !== 0) {
    const jsonObject = {}
    jsonObject['@context'] = 'https://schema.org'
    jsonObject['@type'] = 'BreadcrumbList'
    jsonObject['@id'] = HOST + '/'
    jsonObject['itemListElement'] = []
    breadcrumbs
      ?.filter((item) => item !== 'North America')
      .forEach((breadcrumb, index) => {
        const innerJson = {}
        const itemJson = {}
        innerJson['@type'] = 'ListItem'
        innerJson['position'] = ++index
        itemJson['name'] = breadcrumb
        const breadcrumbUrl = breadcrumb.toLowerCase().replace(' ', '-')
        itemJson['@id'] = `${HOST}/${breadcrumbUrl}/`
        innerJson['item'] = itemJson
        jsonObject['itemListElement'].push(innerJson)
      })
    breadCrumbSchema = jsonObject
  }

  const defaultFAQSchema = generateDefaultCenterFaq(center)
  const filteredFAQSchema = filterFAQs(defaultFAQSchema)
  const faqToDisplay =
    center?.faqs?.length > 0 ? center.faqs : filteredFAQSchema

  const schemaData = centerSchema({ center, location: props.locationData })

  const jsonSchemaMarkup = (
    <script
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schemaData)
      }}
      type="application/ld+json"
    />
  )

  const [sponsorCentersAlgolia, setSponsorCentersAlgolia] = useState<Center[]>(
    []
  )
  const [featuredCentersLoading, setFeaturedCentersLoading] = useState(false)

  useEffect(() => {
    const fetchFeaturedCenters = async () => {
      let queryOptions = {}
      if (isPremiumListing(center)) {
        return
      }
      setFeaturedCentersLoading(true)
      if (
        isNonEmptyObject(center._geoloc) &&
        isNonEmptyString(center._geoloc.lat) &&
        isNonEmptyString(center._geoloc.lng)
      ) {
        queryOptions = {
          aroundRadius: NEARBY_AROUND_RADIUS_IN_METERS,
          aroundLatLng: `${center._geoloc.lat}, ${center._geoloc.lng}`
        }
      }

      const items = await getFeaturedCentersRelatedToCenterLocation({
        center,
        hitsPerPage: MAX_HIT_COUNT,
        primaryFocus: center.primaryFocus,
        ...queryOptions
      }).catch((error) => {
        logger.error({
          error,
          message: 'Error fetching center featured centers'
        })
        return []
      })
      setSponsorCentersAlgolia(items)
      setFeaturedCentersLoading(false)
    }

    fetchFeaturedCenters()
  }, [])

  const pageTitle = center.title
  const pageDescription = center.descriptions?.short
  const canonicalLink = centerUrl

  const socialTagsData = generateSocialTags(
    pageTitle,
    pageDescription,
    'website',
    centerUrl,
    center.featured_image
  )

  return (
    <AppContextProvider
      value={{
        breadcrumbs: [...breadcrumbs, center.title],
        pageType: Page.CENTER_PROFILE
      }}
    >
      <div>
        <App
          pageType={Page.CENTER_PROFILE}
          title={center.title}
          breadcrumbs={breadcrumbs || []}
          keywords={getCenterDataKeywords()}
          showBreadCrumbs={true}
          isLuxury={computeIsLuxury(center)}
          center={center}
          location={props.locationData}
          locationAllowList={props.locationAllowList}
          department={department}
          departments={props.departments}
          loadFurtherAiScript={center.furtherAiEnabled}
        >
          <MetaData
            pageTitle={pageTitle}
            pageDescription={pageDescription}
            canonicalLink={canonicalLink}
            socialTagsData={socialTagsData}
          />
          <CenterHead
            canonicalLink={canonicalLink}
            reviews={props.reviews}
            data={{
              ...center
            }}
          />
          <div />
          <TemplateCenter
            location={props.location.search}
            similarCenters={[]}
            data={center}
            reviews={props.reviews}
            ourCenters={props.ourCenters}
            keywords={getCenterDataKeywords()}
            breadcrumbs={breadcrumbs}
            queryID={queryID}
          />
          {center.phone_numbers &&
            center.claimed_email &&
            (!center.furtherAiEnabled && isContactBarVisible === true ? (
              <ContactBar
                data={center}
                location={props.location.pathname}
                email={center.email}
                phone={getPhoneNumbers(center)[0]}
                title={center.title}
                claimedEmail={center.claimed_email}
                breadcrumbs={breadcrumbs}
                queryID={queryID}
              />
            ) : (
              ''
            ))}
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(breadCrumbSchema)
            }}
            type="application/ld+json"
          />

          {jsonSchemaMarkup}
          <NavMarkers
            standard={center.listing_template !== 'Premium'}
            id="profile-faqs"
          >
            <div className="mx-auto max-w-screen-2xl pt-14 sm:pt-[88px]">
              <div className="mx-auto max-w-screen-xl px-4 md:px-6 lg:px-8">
                <div className="flex flex-col justify-center sm:flex-row sm:gap-[72px] md:flex-row md:gap-[68px] lg:flex-row lg:gap-[106px] xl:flex-row xl:gap-[74px]">
                  <div className="flex flex-col items-start">
                    <div className="font-figtree lg:leading-16 mb-6 flex flex-col gap-2 text-4xl font-normal leading-10 text-gray-1100 lg:mb-7 lg:text-5xl">
                      <span className="w-full sm:min-w-[250px] md:min-w-[300px] lg:min-w-[370px] xl:min-w-[400px]">
                        Frequently
                      </span>
                      <span className="w-full sm:min-w-[250px] md:min-w-[300px] lg:min-w-[370px] xl:min-w-[400px]">
                        Asked Questions
                      </span>
                    </div>
                  </div>
                  <div className="relative w-full md:min-w-[250px] lg:min-w-[300px] xl:min-w-[500px]">
                    {faqToDisplay?.length > 0 ? (
                      <CustomFAQs customFAQs={faqToDisplay} />
                    ) : (
                      <DefaultCenterFAQs
                        data={center}
                        offersDetox={offersDetox}
                        detoxServiceIndex={detoxServiceIndex}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </NavMarkers>
          <Spacer></Spacer>

          {isFreeListing(center) && isNonEmptyArray(sponsorCentersAlgolia) && (
            <CenterCarousel
              centers={sponsorCentersAlgolia}
              zoneId={ZoneIds.Featured}
              isLoading={featuredCentersLoading}
              scrollItemCount={4}
              hideDescription={true}
              sectionHeading="Featured Rehabs"
              containerId="featuredRehabs"
            />
          )}
          {isFreeListing(center) &&
            center._new_specializations?.length > 0 &&
            center.locations?.length > 0 &&
            center._locations[center._locations.length - 1]?._related_locations
              .length > 0 && <RelatedLinks data={center} />}
        </App>
      </div>
    </AppContextProvider>
  )
}

export default CenterComponent
